<script setup>
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
const emit = defineEmits(['export']);
const route = useRoute();
const dialogRef = ref(null);
const segments = useMapGetter('customViews/getContactCustomViews');
const appliedFilters = useMapGetter('contacts/getAppliedContactFilters');
const uiFlags = useMapGetter('contacts/getUIFlags');
const isExportingContact = computed(() => uiFlags.value.isExporting);
const preset = ref('phone_cpf');
const delimiter = ref(';');
const customColumns = ref(['phone_number', 'cpf', 'name']);
const fields = [
  { value: 'phone_number', label: 'Telefone' },
  { value: 'cpf', label: 'CPF' },
  { value: 'name', label: 'Nome' },
  { value: 'email', label: 'E-mail' },
  { value: 'id', label: 'ID' },
  { value: 'labels', label: 'Etiquetas' },
];
const presets = [
  { value: 'phone_cpf', label: 'Telefone e CPF' },
  { value: 'cpf_name', label: 'CPF e nome' },
  { value: 'complete', label: 'Cadastro completo' },
  { value: 'custom', label: 'Escolher colunas' },
];
const columns = computed(() => {
  if (preset.value === 'phone_cpf') return ['phone_number', 'cpf'];
  if (preset.value === 'cpf_name') return ['cpf', 'name'];
  if (preset.value === 'complete')
    return ['id', 'name', 'email', 'phone_number', 'cpf', 'labels'];
  return customColumns.value;
});
const toggleColumn = (key, checked) => {
  customColumns.value = checked
    ? [...customColumns.value, key]
    : customColumns.value.filter(item => item !== key);
};
const exportContacts = () => {
  const segment = segments.value.find(
    view => view.id === Number(route.params.segmentId)
  );
  let query = { payload: [] };
  if (segment) query = segment.query;
  else if (appliedFilters.value.length)
    query = filterQueryGenerator(appliedFilters.value);
  emit('export', {
    ...query,
    label: route.params.label || '',
    q: route.query.search || '',
    active: route.name === 'contacts_dashboard_active',
    column_names: columns.value,
    delimiter: delimiter.value,
  });
};
defineExpose({ dialogRef });
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <Dialog
    ref="dialogRef"
    title="Exportar contatos"
    description="Gere um CSV com os contatos da visualização atual. Acompanhe e baixe o arquivo pela tela, sem envio por e-mail."
    confirm-button-label="Gerar arquivo"
    overflow-y-auto
    :is-loading="isExportingContact"
    :disable-confirm-button="isExportingContact || !columns.length"
    @confirm="exportContacts"
  >
    <div class="grid gap-4">
      <label class="grid gap-2 text-sm text-n-slate-12"
        >Modelo do arquivo<Select
          v-model="preset"
          :options="presets"
          aria-label="Modelo do arquivo"
      /></label>
      <div v-if="preset === 'custom'" class="grid grid-cols-2 gap-3">
        <label
          v-for="field in fields"
          :key="field.value"
          class="flex cursor-pointer items-center gap-2 text-sm text-n-slate-12"
        >
          <Checkbox
            :model-value="customColumns.includes(field.value)"
            @update:model-value="checked => toggleColumn(field.value, checked)"
          />{{ field.label }}
        </label>
      </div>
      <label class="grid gap-2 text-sm text-n-slate-12"
        >Separador<Select
          v-model="delimiter"
          :options="[
            { value: ';', label: 'Ponto e vírgula (;)' },
            { value: ',', label: 'Vírgula (,)' },
          ]"
          aria-label="Separador do CSV"
      /></label>
      <p class="m-0 rounded-lg bg-n-alpha-2 p-3 text-sm text-n-slate-11">
        Colunas, na ordem:
        {{
          columns
            .map(key => fields.find(field => field.value === key)?.label)
            .join(' · ') || 'Selecione uma coluna'
        }}.
      </p>
      <p v-if="route.params.label" class="m-0 text-xs text-n-slate-11">
        Etiqueta: {{ route.params.label }}. Inclui contatos marcados diretamente
        ou em suas conversas, sem duplicação.
      </p>
    </div>
  </Dialog>
</template>
